import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Caption1,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tab,
  TabList,
  Text,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, ArrowSyncRegular, CheckmarkCircleRegular, SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { PageHeader } from '../components/PageHeader';
import type { RoleDriftOverviewDto, RoleOverviewDto, RoleSummaryDto } from '../api/types';
import { IamEditorTab } from './IamEditorTab';
import { IamGraphTab } from './IamGraphTab';
import { IamHeatmapTab } from './IamHeatmapTab';
import './iam.css';

type TabKey = 'overview' | 'editor' | 'graph' | 'heatmap';
type SortCol = 'name' | 'category' | 'access' | 'intune' | 'approles' | 'groups' | 'assignments';

export function IamPage() {
  const api = useApi();
  const [tab, setTab] = useState<TabKey>('overview');
  const [overview, setOverview] = useState<RoleOverviewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const reloadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOverview(await api.getRoleOverview());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reloadOverview();
  }, [reloadOverview]);

  function openInEditor(id: string) {
    setSelectedRoleId(id);
    setTab('editor');
  }

  function newRole() {
    setSelectedRoleId(null);
    setTab('editor');
  }

  return (
    <div>
      <PageHeader
        title="IAM · Rollen"
        description="Rollen bündeln App-Zugriffe und Intune-Installationen (Entra-Gruppen) und werden Personen oder Gruppen zugewiesen. Der Soll-Zustand wird auf Knopfdruck nach Entra geschrieben."
        action={
          <Button appearance="primary" icon={<AddRegular />} onClick={newRole}>
            Neue Rolle
          </Button>
        }
      />

      <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as TabKey)} style={{ marginBottom: 16 }}>
        <Tab value="overview">Übersicht</Tab>
        <Tab value="editor">Editor</Tab>
        <Tab value="graph">Graph</Tab>
        <Tab value="heatmap">Heatmap</Tab>
      </TabList>

      {error ? (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {tab === 'overview' ? (
        <OverviewTab overview={overview} loading={loading} onOpen={openInEditor} />
      ) : null}

      {tab === 'editor' ? (
        <IamEditorTab
          roleId={selectedRoleId}
          onRoleSelected={setSelectedRoleId}
          onChanged={reloadOverview}
          roles={overview?.items ?? []}
        />
      ) : null}

      {tab === 'graph' ? <IamGraphTab onOpen={openInEditor} /> : null}

      {tab === 'heatmap' ? <IamHeatmapTab /> : null}
    </div>
  );
}

function OverviewTab({
  overview,
  loading,
  onOpen,
}: {
  overview: RoleOverviewDto | null;
  loading: boolean;
  onOpen: (id: string) => void;
}) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'name', dir: 'asc' });
  const [driftOv, setDriftOv] = useState<RoleDriftOverviewDto | null>(null);
  const [driftBusy, setDriftBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyMsg, setApplyMsg] = useState<string | null>(null);

  async function checkAllDrift() {
    setDriftBusy(true);
    setApplyMsg(null);
    try {
      setDriftOv(await api.getRoleDriftOverview());
    } catch (e) {
      setApplyMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setDriftBusy(false);
    }
  }

  async function applyAll() {
    setApplyBusy(true);
    setApplyMsg(null);
    try {
      const r = await api.applyAllRoles();
      setApplyMsg(`${r.applied} Mitgliedschaft(en) in ${r.roles} Rolle(n) ergänzt${r.failed ? `, ${r.failed} fehlgeschlagen` : ''}.`);
      setDriftOv(await api.getRoleDriftOverview());
    } catch (e) {
      setApplyMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setApplyBusy(false);
    }
  }

  const rows = useMemo(() => {
    const items = overview?.items ?? [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? items.filter(
          (r) => r.name.toLowerCase().includes(q) || (r.category ?? '').toLowerCase().includes(q),
        )
      : items;
    const val = (r: RoleSummaryDto): string | number => {
      switch (sort.col) {
        case 'name':
          return r.name.toLowerCase();
        case 'category':
          return (r.category ?? '').toLowerCase();
        case 'access':
          return r.access;
        case 'intune':
          return r.intune;
        case 'approles':
          return r.appRoles;
        case 'groups':
          return r.groups;
        case 'assignments':
          return r.assignments;
      }
    };
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sort.dir === 'asc' ? c : -c;
    });
  }, [overview, search, sort]);

  function head(label: string, col: SortCol) {
    const active = sort.col === col;
    return (
      <th onClick={() => setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }))}>
        {label}
        {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </th>
    );
  }

  if (loading && !overview) {
    return <Spinner label="Lade Rollen…" />;
  }

  const maxCat = Math.max(1, ...(overview?.byCategory ?? []).map((c) => c.count));

  return (
    <div>
      <div className="iam-kpis">
        <Kpi value={overview?.roles ?? 0} label="Rollen" />
        <Kpi value={overview?.assignments ?? 0} label="Zuweisungen" />
        <Kpi value={overview?.people ?? 0} label="Personen mit Rolle" />
        <Kpi value={overview?.avgRolesPerPerson ?? 0} label="Ø Rollen / Person" />
      </div>

      <div className="iam-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text weight="semibold">Soll-Ist-Abgleich</Text>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>über alle aktiven Rollen</Caption1>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button icon={<ArrowSyncRegular />} disabled={driftBusy} onClick={checkAllDrift}>
              {driftBusy ? 'Prüfe…' : 'Drift prüfen'}
            </Button>
            {driftOv && driftOv.rolesWithDrift > 0 ? (
              <Button appearance="primary" icon={<CheckmarkCircleRegular />} disabled={applyBusy} onClick={applyAll}>
                {applyBusy ? 'Wende an…' : 'Alle anwenden'}
              </Button>
            ) : null}
          </div>
        </div>
        {applyMsg ? (
          <MessageBar intent="success" style={{ marginTop: 10 }}>
            <MessageBarBody>{applyMsg}</MessageBarBody>
          </MessageBar>
        ) : null}
        {driftOv ? (
          driftOv.rolesWithDrift === 0 ? (
            <Caption1 style={{ color: tokens.colorPaletteGreenForeground1, display: 'block', marginTop: 10 }}>
              Keine offene Abweichung – Soll = Ist.
            </Caption1>
          ) : (
            <div style={{ marginTop: 10 }}>
              <Caption1 style={{ color: tokens.colorPaletteDarkOrangeForeground1 }}>
                {driftOv.rolesWithDrift} Rolle(n) mit {driftOv.totalMissing} offenen Mitgliedschaft(en):
              </Caption1>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {driftOv.items.map((it) => (
                  <span key={it.roleId} className="iam-chip" style={{ cursor: 'pointer' }} onClick={() => onOpen(it.roleId)} title="Im Editor öffnen">
                    {it.roleName} · {it.count}
                  </span>
                ))}
              </div>
            </div>
          )
        ) : null}
      </div>

      <div className="iam-cols">
        <div className="iam-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
            <Text weight="semibold">Rollen</Text>
            <Input
              style={{ width: 240, marginLeft: 'auto' }}
              contentBefore={<SearchRegular />}
              placeholder="Rolle oder Kategorie suchen…"
              value={search}
              onChange={(_, d) => setSearch(d.value)}
            />
          </div>

          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <table className="iam-table">
              <thead>
                <tr>
                  {head('Rolle', 'name')}
                  {head('Kategorie', 'category')}
                  {head('App-Zugriffe', 'access')}
                  {head('Intune', 'intune')}
                  {head('App-Rollen', 'approles')}
                  {head('Gruppen', 'groups')}
                  {head('Zuweisungen', 'assignments')}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => onOpen(r.id)}>
                    <td>
                      <span className="pii" style={{ fontWeight: 500 }}>{r.name}</span>
                      {!r.enabled ? (
                        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}> · inaktiv</Caption1>
                      ) : null}
                    </td>
                    <td>{r.category ?? <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>}</td>
                    <td><span className="iam-pill">{r.access}</span></td>
                    <td><span className="iam-pill">{r.intune}</span></td>
                    <td><span className="iam-pill">{r.appRoles}</span></td>
                    <td><span className="iam-pill">{r.groups}</span></td>
                    <td><span className="iam-pill">{r.assignments}</span></td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ color: tokens.colorNeutralForeground3, textAlign: 'center', padding: 24 }}>
                      Noch keine Rollen — oben rechts „Neue Rolle" anlegen.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="iam-card">
          <Text weight="semibold">Nach Kategorie</Text>
          <div style={{ marginTop: 10 }}>
            {(overview?.byCategory ?? []).map((c) => (
              <div className="iam-bar-row" key={c.category}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.category}>
                  {c.category}
                </span>
                <span className="iam-bar-track">
                  <span className="iam-bar-fill" style={{ width: `${(c.count / maxCat) * 100}%` }} />
                </span>
                <span style={{ textAlign: 'right', color: tokens.colorNeutralForeground3 }}>{c.count}</span>
              </div>
            ))}
            {(overview?.byCategory ?? []).length === 0 ? (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Keine Daten.</Caption1>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ value, label }: { value: number; label: string }) {
  return (
    <div className="iam-kpi">
      <div className="iam-kpi-val">{value}</div>
      <div className="iam-kpi-lbl">{label}</div>
    </div>
  );
}
